import { z } from 'zod'

import { StudentAttendanceGroupSchema } from '~/@entities/Attendance'
import { StudentIdSchema, StudentPhoneGroupSchema } from '~/@entities/Student'
import { TeacherIdSchema, TeacherSchema } from '~/@entities/Teacher'

// 반 고유 ID. 서버 PK(Int) 그대로다.
export const LessonClassIdSchema = z.number()
export type LessonClassId = z.infer<typeof LessonClassIdSchema>

/***************** 반 목록 *****************/
// /lesson-classes 의 한 반(LessonClassResponse). 학생·선생님 이름 예시는 반의 첫 재원 학생·첫 선생님 이름이고
// 나머지 인원 수가 함께 온다. 재원 학생이나 선생님이 없으면 이름 예시가 null 이다.
// index 는 반 순서이고 서버가 이 값으로 정렬해 낸다.
export const LessonClassSchema = z.object({
  id: LessonClassIdSchema,
  name: z.string(),
  index: z.number(),
  studentNameExample: z.string().nullable(),
  remainStudentCount: z.number(),
  teacherNameExample: z.string().nullable(),
  remainTeacherCount: z.number(),
})
export type LessonClass = z.infer<typeof LessonClassSchema>

/***************** 반 상세 *****************/
// /lesson-classes/{id} 응답(LessonClassDetailResponse). 담당 선생님과 소속 학생 ID 목록을 싣는다.
// grade 는 반을 만들·고칠 때 받는 학년인데, 선생님 앱은 보내지 않아 서버가 0 으로 저장한다(생성·수정 요청 함수 주석).
export const LessonClassDetailSchema = LessonClassSchema.pick({
  id: true,
  name: true,
  index: true,
}).extend({
  grade: z.number(),
  teacherIdList: z.array(TeacherIdSchema),
  studentIdList: z.array(StudentIdSchema),
})
export type LessonClassDetail = z.infer<typeof LessonClassDetailSchema>

/***************** 반을 맡은 선생님 *****************/
// /lesson-classes/teacher 응답(AssignedTeacherListResponse). 반 관리 화면의 선생님 필터 목록이다.
export const LessonClassTeacherSchema = z.object({
  teacherId: TeacherIdSchema,
  name: TeacherSchema.shape.name,
})
export type LessonClassTeacher = z.infer<typeof LessonClassTeacherSchema>

/***************** 반별 학생 묶음 *****************/
// /lesson-classes/{attendance,phone} 이 내는 LessonClassListWithMemberResponse. 멤버는 /students/* 의 학교급·학년 묶음과
// 같은 하위 클래스(StudentMemberWith*Response)라 그쪽 스키마의 멤버를 그대로 쓴다.
const lessonClassGroupSchema = <Member extends z.ZodType>(memberList: Member) =>
  z.object({
    id: LessonClassIdSchema,
    name: LessonClassSchema.shape.name,
    memberList,
  })

// 오늘 출결과 이메일을 실은 학생 묶음
export const LessonClassAttendanceGroupSchema = lessonClassGroupSchema(
  StudentAttendanceGroupSchema.shape.memberList,
)
export type LessonClassAttendanceGroup = z.infer<typeof LessonClassAttendanceGroupSchema>

// 학생·보호자 전화번호를 실은 학생 묶음
export const LessonClassPhoneGroupSchema = lessonClassGroupSchema(
  StudentPhoneGroupSchema.shape.memberList,
)
export type LessonClassPhoneGroup = z.infer<typeof LessonClassPhoneGroupSchema>
